import {
  baseState,
  ContractService,
  ContractTypeService,
  createState,
  editState,
  gridState,
  infoRedirectState,
  infoState,
} from "./ContractService";

import { vsServiceName as contractServiceName } from "./ContractService";
import {
  StateParams,
  StateProvider,
  StateRegistry,
  StateService,
} from "@uirouter/angularjs";
import {
  GridConfiguration,
  vsComponentName as RecordGridViewComponentName,
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../../records/views/recordGridView";
import { vsComponentName as machineDetailContractComponentName } from "../views/info/names";
import {
  vsComponentName as FormViewComponentName,
  FormViewService,
  vsServiceName as FormViewServiceName,
  FormSubmitArgs,
} from "../../records/forms/formView";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../records/grids/columns/columnDefinitionService";
import { ChangeJournalEntry } from "../../records/changeJournal/changeJournalViewComponent/models";
import {
  ChangeJournalModalService,
  vsServiceName as ChangeJournalModalServiceName,
} from "../../records/changeJournal/changeNoteModalService";

registerRoutes.$inject = ["$stateProvider"];
export function registerRoutes($stateProvider: StateProvider) {
  $stateProvider
    .state({
      name: baseState,
      url: "/:type",
      abstract: true,
      params: {
        type: undefined,
      },
      resolve: {
        contractType: [
          contractServiceName,
          "$stateParams",
          (contractService: ContractService, $stateParams: StateParams) =>
            contractService.getContractType($stateParams.type),
        ],
      },
    })
    .state({
      name: createState,
      url: "/create",
      component: FormViewComponentName,
      params: {
        modelId: undefined,
      },
      resolve: FormViewService.getResolveObjectFromFormViewConfig(
        [
          "contractType",
          ChangeJournalModalServiceName,
          FormViewServiceName,
          "$state",
          (
            contractType: ContractTypeService,
            changeJournalModalService: ChangeJournalModalService,
            formViewService: FormViewService,
            $state: StateService
          ) => {
            const formViewConfig = formViewService.getFormViewConfig(
              contractType.formConfigName
            )!;

            formViewConfig.submitForm = (args: FormSubmitArgs) => {
              return formViewService
                .saveForm({
                  formObject: args.model,
                  formViewController: args.formViewController,
                  url: contractType.getBaseUrl(),
                })

                .then((response: any) => {
                  const ContractInstanceId =
                    response?.Result?.ContractInstanceId ||
                    response?.ContractInstanceId;
                  if (!ContractInstanceId) {
                    throw new Error(
                      "No contractInstanceId found in save response"
                    );
                  }

                  return $state.go(infoRedirectState, {
                    contractInstanceId: ContractInstanceId,
                  });
                });
            };

            return formViewConfig;
          },
        ],
        [
          "$stateParams",
          "contractType",
          ($stateParams: StateParams, contractType: ContractTypeService) => {
            const { modelId } = $stateParams;

            if (!modelId) {
              return {};
            }

            return contractType.cloneContract(modelId);
          },
        ]
      ),
    })
    .state({
      name: editState,
      url: "/edit",
      views: {
        "": { component: FormViewComponentName },
      },
      resolve: {
        ...FormViewService.getResolveObjectFromFormViewConfig(
          [
            "contractType",
            ChangeJournalModalServiceName,
            FormViewServiceName,
            "$state",
            (
              contractType: ContractTypeService,
              changeJournalModalService: ChangeJournalModalService,
              formViewService: FormViewService,
              $state: StateService
            ) => {
              const formViewConfig = formViewService.getFormViewConfig(
                contractType.formConfigName
              )!;

              formViewConfig.submitForm = (args: FormSubmitArgs) => {
                return changeJournalModalService
                  .getAndSetChangeJournalNote(args.model)
                  .then(() => {
                    return formViewService.saveForm({
                      formObject: args.model,
                      formViewController: args.formViewController,
                      url: contractType.getBaseUrl(),
                    });
                  })
                  .then(() => {
                    return $state.go("^.redirect");
                  });
              };

              return formViewConfig;
            },
          ],
          ["contract", (contract: any) => contract]
        ),
      },
    })
    .state({
      name: gridState,
      url: "/grid",
      component: RecordGridViewComponentName,
      resolve: {
        ...RecordGridViewService.getResolveObjectFromGridConfiguration([
          "contractType",
          RecordGridViewServiceName,
          (
            contractType: ContractTypeService,
            recordGridViewService: RecordGridViewService
          ) => {
            const gridConfiguration =
              recordGridViewService.getGridConfiguration(
                contractType.gridConfigName
              );
            if (!gridConfiguration) {
              throw new Error("Couldn't resolve grid configuration.");
            }
            gridConfiguration.onAddNew = () =>
              contractType.createNavigate().then(() => {});

            return gridConfiguration;
          },
        ]),
        defaultQuery: [() => ({ Include: "ContractNumbers" })],
        columns: [
          "contractType",
          "gridConfiguration",
          ColumnDefinitionServiceName,
          (
            contractType: ContractTypeService,
            gridConfiguration: GridConfiguration,
            columnDefinitionService: ColumnDefinitionService
          ) => [
            columnDefinitionService.getLinkColDef({
              colId: "info",
              headerName: "Info",
              iconClass: "fa fa-info-circle text-primary",
              stateExpression: `${contractType.infoSref()}({contractInstanceId: data.ContractInstanceId})`,
            }),
            columnDefinitionService.getLinkColDef({
              colId: "edit",
              headerName: "Edit",
              iconClass: "fa fa-pencil text-primary",
              stateExpression: `${contractType.editSref()}({contractInstanceId: data.ContractInstanceId, type: data.ContractInstanceType})`,
              requiresPermission: "contractEdit",
            }),
            ...(gridConfiguration.columns ?? []),
          ],
        ],
      },
    })
    .state({
      name: infoState,
      url: "/{contractInstanceId:int}",
      component: machineDetailContractComponentName,
      resolve: {
        contract: [
          "$stateParams",
          "contractType",
          ($stateParams: StateParams, contractType: ContractTypeService) =>
            contractType.fetchContract($stateParams.contractInstanceId),
        ],
      },
    })
    .state({
      name: infoRedirectState,
      onEnter: [
        "$state",
        "contractType",
        "$stateParams",
        (
          $state: StateService,
          contractType: ContractTypeService,
          $stateParams: StateParams
        ) =>
          $state.target(contractType.infoSref(), {
            type: $stateParams.type,
            contractInstanceId: $stateParams.contractInstanceId,
          }),
      ],
    });
}
